import {useMemo} from 'react';
import { AvailabilityObject } from '../types/schemas/abc';
import {
  getTimezoneOffset,
  adjustAvailabilityForTimezone,
  unrollAvailability,
  GRANULARITY,
  DAYS_MONDAY_FIRST,
} from '../utils/availability';


interface AvailabilityInput {
    availability?: AvailabilityObject;
    timezone?: string;
}


export const useAvailability = (
    user1: AvailabilityInput,
    user2: AvailabilityInput
  ) => {

    const availabilityStatusMap = useMemo(() => {
        const statusMap = new Map<string, 'overlap' | 'user1Only' | 'user2Only'>();
        if (
            !user1.availability ||
            !user2.availability ||
            !user1.timezone ||
            !user2.timezone
        ) {
            return statusMap;
        }

        const offset = getTimezoneOffset(user1.timezone, user2.timezone);
        const adjustedUser2Availability = adjustAvailabilityForTimezone(
          user2.availability, offset, DAYS_MONDAY_FIRST
        );

        const user1Slots = unrollAvailability(user1.availability);
        const user2Slots = unrollAvailability(adjustedUser2Availability);
        const allSlots = new Set([...Array.from(user1Slots), ...Array.from(user2Slots)]);

        allSlots.forEach((slot) => {
        const user1Has = user1Slots.has(slot);
        const user2Has = user2Slots.has(slot);

        if (user1Has && user2Has) {
            statusMap.set(slot, 'overlap');
        } else if (user1Has) {
            statusMap.set(slot, 'user1Only');
        } else {
            statusMap.set(slot, 'user2Only');
        }
        })

        return statusMap;
    }, [user1.availability, user1.timezone, user2.availability, user2.timezone]);
    
    const hoursOfDay = useMemo(() => {
        if (availabilityStatusMap.size === 0) return [];
    
        const minutes = new Set<number>();
        availabilityStatusMap.forEach((_, slotKey) => {
          const minute = parseInt(slotKey.split('-')[1], 10);
          if (!isNaN(minute)) {
            minutes.add(minute);
          }
        });
    
        if (minutes.size === 0) return [];
    
        const minMinute = Math.min(...Array.from(minutes));
        const maxMinute = Math.max(...Array.from(minutes));
    
        // Generate slots based on GRANULARITY
        const generatedSlots = [];
        for (let i = minMinute; i <= maxMinute; i += GRANULARITY) {
          generatedSlots.push(i);
        }
        return generatedSlots.sort((a, b) => a - b);
      }, [availabilityStatusMap]);
    
      return { availabilityStatusMap, hoursOfDay };
  }